import { type App } from 'vue';
import {
  createRouter,
  createWebHistory,
  type Router,
  type RouteRecordRaw,
} from 'vue-router';

const prefixes = import.meta.glob<{ default: string }>(
  '../modules/*/prefix.ts',
  { eager: true },
);

const routeLoaders = import.meta.glob<{ routes: RouteRecordRaw[] }>(
  '../modules/*/routes.ts',
);

type ModuleEntry = {
  name: string;
  prefix: string;
  loadRoutes: () => Promise<{ routes: RouteRecordRaw[] }>;
};

function buildModuleRegistry(): ModuleEntry[] {
  const entries: ModuleEntry[] = [];

  for (const [path, mod] of Object.entries(prefixes)) {
    const name = path.match(/modules\/([^/]+)\/prefix\.ts/)?.[1];
    if (!name) continue;

    const routeKey = Object.keys(routeLoaders).find((k) =>
      k.includes(`/modules/${name}/routes.ts`),
    );
    if (!routeKey) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(
          `[router] Module "${name}" has prefix.ts but no routes.ts`,
        );
      }
      continue;
    }

    entries.push({
      name,
      prefix: mod.default,
      loadRoutes: routeLoaders[routeKey]!,
    });
  }

  // Longer prefixes first so "/pricing/deals-lp-1001" matches before "/pricing/deals"
  entries.sort((a, b) => b.prefix.length - a.prefix.length);

  return entries;
}

function stripLangPrefix(pathname: string): string {
  return pathname.replace(/^\/[a-z]{2}(?=\/)/, '');
}

const moduleRegistry = buildModuleRegistry();

function resolveModule(pathname: string): ModuleEntry | undefined {
  const normalized = stripLangPrefix(pathname);
  return moduleRegistry.find((entry) => normalized.startsWith(entry.prefix));
}

function getNotFoundLoader() {
  const key = Object.keys(routeLoaders).find((k) =>
    k.includes('/modules/not-found/routes.ts'),
  );
  return key ? routeLoaders[key] : undefined;
}

if (import.meta.env.DEV) {
  const registeredModules = new Set(moduleRegistry.map((e) => e.name));
  registeredModules.add('not-found');

  for (const key of Object.keys(routeLoaders)) {
    const name = key.match(/modules\/([^/]+)\/routes\.ts/)?.[1];
    if (name && !registeredModules.has(name)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[router] Module "${name}" has routes.ts but no prefix.ts — its routes will never load.`,
      );
    }
  }
}

export default async function setupRouter(app: App): Promise<Router> {
  const matched = resolveModule(window.location.pathname);
  const notFoundLoader = getNotFoundLoader();

  const [moduleRoutes, notFoundRoutes] = await Promise.all([
    matched ? matched.loadRoutes().then((m) => m.routes) : Promise.resolve([]),
    notFoundLoader
      ? notFoundLoader().then((m) => m.routes)
      : Promise.resolve([]),
  ]);

  const router = createRouter({
    history: createWebHistory(),
    routes: [...moduleRoutes, ...notFoundRoutes],
    scrollBehavior() {
      return { top: 0 };
    },
  });

  const loadedModules = new Set<string>(
    matched ? [matched.name, 'not-found'] : ['not-found'],
  );

  router.beforeEach(async (to) => {
    if (to.matched.length > 0) return;

    const target = resolveModule(to.path);
    if (target && !loadedModules.has(target.name)) {
      const mod = await target.loadRoutes();
      mod.routes.forEach((r) => router.addRoute(r));
      loadedModules.add(target.name);
      return to.fullPath;
    }
  });

  app.use(router);
  await router.isReady();

  return router;
}
